
import { useEffect, useRef, useState } from "react"

import { get as idb_get } from "idb-keyval"
import { io } from "socket.io-client"

import { useGlobal } from "../../../contexts/GlobalContext"
import { mixpanel_client_track } from "../../../libs/mixpanelClient"

const kpa1_tick_limit = 200

const format_tick_price = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return "—"
  }
  return n.toFixed(4)
}

const format_tick_time = (ts_ms) => {
  if (!ts_ms) {
    return "—"
  }
  try {
    return new Date(ts_ms).toLocaleTimeString()
  }
  catch {
    return "—"
  }
}

const KPA1Page = () => {
  const global_context = useGlobal()

  const socket_ref = useRef<any>(null)

  const [kalshi_ticker_input, set_kalshi_ticker_input] = useState("")
  const [polymarket_slug_input, set_polymarket_slug_input] = useState("")

  const [is_connecting, set_is_connecting] = useState(false)
  const [is_live_running, set_is_live_running] = useState(false)
  const [status_text, set_status_text] = useState("idle")
  const [error_text, set_error_text] = useState("")

  const [kalshi_latest_tick, set_kalshi_latest_tick] = useState<any>(null)
  const [kalshi_ticks, set_kalshi_ticks] = useState<any[]>([])
  const [polymarket_latest_tick, set_polymarket_latest_tick] = useState<any>(null)
  const [polymarket_ticks, set_polymarket_ticks] = useState<any[]>([])

  useEffect(() => {
    mixpanel_client_track("app_dashboard_kpa1_visited", global_context.user_id)

    return () => {
      if (socket_ref.current) {
        socket_ref.current.disconnect()
        socket_ref.current = null
      }
    }
  }, [global_context.user_id])

  const ensure_socket_connected = async () => {
    if (socket_ref.current && socket_ref.current.connected) {
      return socket_ref.current
    }

    const session_token = await idb_get("session_token")
    const socket = io("/", {
      path: "/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      auth: {
        session_token: session_token,
      },
    })

    socket_ref.current = socket

    socket.on("connect", () => {
      set_status_text("connected")
    })
    socket.on("disconnect", () => {
      set_status_text("disconnected")
      set_is_live_running(false)
    })
    socket.on("kpa1_error", (payload) => {
      set_error_text(payload?.status || "KPA1_SOCKET_ERROR")
      set_is_connecting(false)
    })
    socket.on("kpa1_status", (payload) => {
      if (payload?.status === "SUBSCRIBED") {
        set_is_live_running(true)
        set_is_connecting(false)
        set_status_text("live")
      }
      else if (payload?.status === "UNSUBSCRIBED") {
        set_is_live_running(false)
        set_is_connecting(false)
        set_status_text("idle")
      }
      else if (payload?.status) {
        set_status_text(String(payload.status).toLowerCase())
      }
    })
    socket.on("kpa1_tick", (tick) => {
      if (tick?.provider === "kalshi") {
        set_kalshi_latest_tick(tick)
        set_kalshi_ticks((prev) => [tick, ...prev].slice(0, kpa1_tick_limit))
      }
      else if (tick?.provider === "polymarket") {
        set_polymarket_latest_tick(tick)
        set_polymarket_ticks((prev) => [tick, ...prev].slice(0, kpa1_tick_limit))
      }
    })

    await new Promise<void>((resolve, reject) => {
      socket.once("connect", () => resolve())
      socket.once("connect_error", (error) => reject(error))
    })

    return socket
  }

  const start_live_feed = async () => {
    const kalshi_ticker = kalshi_ticker_input.trim()
    const polymarket_slug = polymarket_slug_input.trim()

    if (!kalshi_ticker || !polymarket_slug) {
      set_error_text("Enter both Kalshi market ticker and Polymarket market slug.")
      return
    }

    set_error_text("")
    set_is_connecting(true)

    try {
      const socket = await ensure_socket_connected()

      set_kalshi_ticks([])
      set_polymarket_ticks([])
      set_kalshi_latest_tick(null)
      set_polymarket_latest_tick(null)

      socket.emit("kpa1_subscribe", {
        kalshi_ticker: kalshi_ticker,
        polymarket_slug: polymarket_slug,
      })
    }
    catch (error) {
      const msg = error?.message || "Socket connection failed."
      set_error_text(msg)
      set_is_connecting(false)
      set_is_live_running(false)
      set_status_text("error")
    }
  }

  const stop_live_feed = () => {
    set_error_text("")
    const socket = socket_ref.current
    if (!socket) {
      set_is_live_running(false)
      set_status_text("idle")
      return
    }
    socket.emit("kpa1_unsubscribe")
  }

  return (
    <div className="px-4 py-8 pb-32 md:py-16 sm:px-6 md:flex-auto md:px-0 md:py-8">
      <div className="mx-auto max-w-5xl space-y-8 md:mx-0 md:max-w-none">
        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">KPA1</h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Side-by-side live market tape.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500">Kalshi market ticker</label>
              <input
                type="text"
                value={kalshi_ticker_input}
                onChange={(e) => set_kalshi_ticker_input(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="KX..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500">Polymarket market slug</label>
              <input
                type="text"
                value={polymarket_slug_input}
                onChange={(e) => set_polymarket_slug_input(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="market-slug"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
              onClick={() => void start_live_feed()}
              disabled={is_connecting}
            >
              {is_connecting ? "Starting..." : "Start live feed"}
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:border-red-300 hover:text-red-700"
              onClick={stop_live_feed}
            >
              Stop
            </button>
            <span className="text-xs font-semibold uppercase text-gray-500">Status: {status_text}</span>
            <span className="text-xs font-semibold uppercase text-gray-500">Live: {is_live_running ? "on" : "off"}</span>
          </div>

          {error_text ? (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error_text}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">Kalshi</h3>
            <p className="mt-1 text-xs text-gray-500">{kalshi_ticker_input.trim() || "No ticker set"}</p>

            <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="text-xs uppercase text-gray-500">Latest price</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{format_tick_price(kalshi_latest_tick?.price)}</div>
              <div className="mt-1 text-xs text-gray-500">{format_tick_time(kalshi_latest_tick?.ts_ms)}</div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase text-gray-500">Recent ticks</div>
              <div className="max-h-96 overflow-auto rounded-md border border-gray-100">
                {kalshi_ticks.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No live ticks yet.</div>
                ) : (
                  kalshi_ticks.map((tick, idx) => (
                    <div key={`${tick.ts_ms}-${idx}`} className="flex items-center justify-between border-b border-gray-100 px-3 py-2 text-sm">
                      <span className="font-medium text-gray-900">{format_tick_price(tick.price)}</span>
                      <span className="text-xs text-gray-500">{format_tick_time(tick.ts_ms)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-900">Polymarket</h3>
            <p className="mt-1 text-xs text-gray-500">{polymarket_slug_input.trim() || "No slug set"}</p>

            <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 p-3">
              <div className="text-xs uppercase text-gray-500">Latest price</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{format_tick_price(polymarket_latest_tick?.price)}</div>
              <div className="mt-1 text-xs text-gray-500">
                {format_tick_time(polymarket_latest_tick?.ts_ms)}
                {polymarket_latest_tick?.outcome ? ` • ${polymarket_latest_tick.outcome}` : ""}
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase text-gray-500">Recent ticks</div>
              <div className="max-h-96 overflow-auto rounded-md border border-gray-100">
                {polymarket_ticks.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No live ticks yet.</div>
                ) : (
                  polymarket_ticks.map((tick, idx) => (
                    <div key={`${tick.ts_ms}-${idx}`} className="flex items-center justify-between border-b border-gray-100 px-3 py-2 text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{format_tick_price(tick.price)}</span>
                        <span className="text-xs text-gray-500">{tick?.outcome || tick?.asset_id || "asset"}</span>
                      </div>
                      <span className="text-xs text-gray-500">{format_tick_time(tick.ts_ms)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default KPA1Page